import * as is from 'utilities/is';
import device from "current-device";
import debounce from 'debounce';
import { throttle } from 'utilities/time';
import { APPLICATION_EVENT, PLAYER_EVENT } from 'constants/events';
import { PLAY_STATES, DEVICE_TYPES } from 'constants';
import {
    fullscreen,
    deviceType,
    orientation,
    inCrossDomainIframe,
    getViewportWidth,
    getViewportHeight,
    getLanguage
} from 'utilities/context';
import { ANNOTATION_EVENT } from "../../constants/events";

export class ContextWatcher {
    constructor(initialValue, eventBus) {
        this.initialValue = initialValue;
        this.eventBus = eventBus;
        this.notifiers = [];
    }

    onChange(notifier) {
        this.notifiers.push(notifier);
    }

    broadcastChange = () => {
        this.notifiers.forEach(n => n());
    }

    destroy() {
        this.notifiers.length = 0;
    }

    get state() {
        return '';
    }

    set state(value) {
        return;
    }
}

export class FullscreenContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        fullscreen.onChange(this.broadcastChange);
    }

    destroy(...args) {
        super.destroy(...args);
        fullscreen.offChange(this.broadcastChange);
    }

    get state() {
        return fullscreen.inFullscreen;
    }
}

export class DeviceTypeContextWatcher extends ContextWatcher {
    get state() {
        return deviceType;
    }
}

export class OrientationContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.orientation = this.initialValue || orientation;

        if (
            !inCrossDomainIframe || // orientation information blocked in cross domain, so we have rely on external provider (see iframe_helper.js)
            deviceType === DEVICE_TYPES.DESKTOP // device library reports orientation information for desktop using window/height, but we only care about orientation for mobile
        ) {
            device.onChangeOrientation(this.onOrientationChange);
        }
        
    }

    onOrientationChange = newOrientation => {
        this.orientation = newOrientation;
        this.broadcastChange();
    }

    get state() {
        return this.orientation;
    }

    set state(orientation) {
        if (this.orientation !== orientation) {
            this.orientation = orientation;
            this.broadcastChange();
        }
    }
}

export class ViewportWidthContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.width = this.initialValue || getViewportWidth();
        window.addEventListener('resize', this.onResize);
    }

    onResize = debounce(e => {
        this.width = getViewportWidth();
        this.broadcastChange();
    }, 250)

    destroy(...args) {
        super.destroy(...args);
        window.removeEventListener('resize', this.onResize);
    }

    get state() {
        return this.width;
    }
}

export class ViewportHeightContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.height = this.initialValue || getViewportHeight();
        window.addEventListener('resize', this.onResize);
    }

    onResize = debounce(e => {
        this.height = getViewportHeight();
        this.broadcastChange();
    }, 250)

    destroy(...args) {
        super.destroy(...args);
        window.removeEventListener('resize', this.onResize);
    }

    get state() {
        return this.height;
    }
}

export class LanguageContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.language = this.initialValue || getLanguage();
    }

    get state() {
        return this.language;
    }

    set state(language) {
        this.language = language;
        this.broadcastChange();
    }
}

export class PlayStateContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.playState = this.initialValue || PLAY_STATES.PAUSED;
    }

    get state() {
        return this.playState;
    }

    set state(playState) {
        if (this.playState !== playState) {
            this.playState = playState;
            this.broadcastChange();
        }
    }
}

export class VariablesContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.variableState = this.initialValue || {};
        this.eventBus.on(APPLICATION_EVENT.SET_VARIABLES, this.onSetVariables);
    }

    onSetVariables = (updates = {}) => this.state = updates;

    get state() {
        return this.variableState;
    }

    set state(updates = {}) {
        this.variableState = {
            ...this.variableState,
            ...updates
        };

        this.broadcastChange();
    }
}

export class FormDataContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.formState = this.initialValue || {};
        this.eventBus.on(APPLICATION_EVENT.FORM_DATA_CHANGE, this.onFormDataChange);
    }

    onFormDataChange = (updates = {}) => {
        if (!is.equal(this.state, updates, { sortArrays: true })) {
            this.state = updates;
        }
    };

    get state() {
        return this.formState;
    }

    set state(updates = {}) {
        this.formState = (
            Object
                .keys(updates)
                .reduce((state, key) => {
                    state[key] = (
                        state[key] ?
                            { ...state[key], ...updates[key] } :
                            { ...updates[key] }
                    )

                    return state;
                }, { ...this.formState })
        )

        this.broadcastChange();
    }
}

export class ScoreContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.score = isNaN(this.initialValue) ? 0 : this.initialValue;
        this.eventBus.on(APPLICATION_EVENT.SET_SCORE, this.onSetScore);
        this.eventBus.on(APPLICATION_EVENT.INCREMENT_SCORE, this.onIncrementScore);
        this.eventBus.on(APPLICATION_EVENT.DECREMENT_SCORE, this.onDecrementScore);
    }

    onSetScore = (amount = 0) => {
        this.state = Number(amount);
    }

    onIncrementScore = (amount = 1) => {
        this.state = this.score + Number(amount);
    }

    onDecrementScore = (amount = 1) => {
        this.state = this.score - Number(amount);
    }

    get state() {
        return this.score;
    }

    set state(value) {
        if (isNaN(value)) {
            return;
        }

        this.score = value;
        this.broadcastChange();
    }
}

export class PlayerContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.playerState = this.initialValue || this.defaultState;
        this.eventBus.on(PLAYER_EVENT.PLAY, this.onPlay);
        this.eventBus.on(PLAYER_EVENT.PAUSE, this.onPause);
        this.eventBus.on(PLAYER_EVENT.TIME_UPDATE, this.onTimeUpdate);
        this.eventBus.on(PLAYER_EVENT.SIZE_CHANGE, this.onSizeChange);
        this.eventBus.on(PLAYER_EVENT.LOADED_METADATA, this.onLoadedMetadata);
    }

    onPlay = () => this.state = { playing: true, paused: false }
    onPause = () => this.state = { playing: false, paused: true }
    onSizeChange = (event, player) => this.state = { ...(player.containerDimensions || {}) }
    onLoadedMetadata = (event, player) => this.state = { duration: player.duration }

    onTimeUpdate = throttle((event, player) => {
        this.state = { currentTime: player.currentTime }
    }, this.TIME_UPDATE_FREQUENCY_MS)

    get state() {
        return this.playerState;
    }

    set state(updates = {}) {
        this.playerState = {
            ...this.playerState,
            ...updates
        };

        this.broadcastChange();
    }

    get defaultState() {
        return {
            duration: Infinity,
            currentTime: 0,
            playing: false,
            paused: true
        }
    }

    // prevent time update from triggering re-compile too often
    get TIME_UPDATE_FREQUENCY_MS() {
        return 1000;
    }
}

export class UserAgentContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.userAgent = this.initialValue || (navigator && navigator.userAgent);
    }

    get state() {
        return this.userAgent;
    }
}

export class QuizContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.quizState = {};
        this.eventBus.on(ANNOTATION_EVENT.QUIZ_UPDATED, this.onQuizUpdated);
    }

    onQuizUpdated = ({ id, updates = {}}) => {
        this.state = {
            [id]: {
                ...(this.quizState[id] || {}),
                ...updates
            }
        }
    }

    get state() {
        return this.quizState;
    }

    set state(updates = {}) {
        this.quizState = { ...this.quizState, ...updates };
        this.broadcastChange();
    }
}

export class AnnotationContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.annotationState = {};
        this.eventBus.on(ANNOTATION_EVENT.STATE_CHANGE, this.onAnnotationUpdated);
    }

    onAnnotationUpdated = (id, state) => {
        this.state = {
            [id]: { ...state } // destructive state, ensures any deletes to parent state are incorporated
        }
    }

    set state(updates = {}) {
        this.annotationState = {
            ...this.annotationState,
            ...updates
        }
        this.broadcastChange();
    }

    get state() {
        return { ...this.annotationState };
    }
}

export class LevelContextWatcher extends ContextWatcher {
    constructor(...args) {
        super(...args);
        this.level = this.initialValue;
        this.eventBus.on(APPLICATION_EVENT.LEVEL_CHANGED, this.onLevelChanged);
    }

    onLevelChanged = level => this.state = level

    get state() {
        return this.level;
    }

    set state(level) {
        if (is.defined(level) && this.state !== level) {
            this.level = level;
            this.broadcastChange();
        }
    }
}